import { gql } from '@apollo/client';

// --- Fragments ---

export const ASSET_LIST_FIELDS = gql`
  fragment AssetListFields on Asset {
    _id
    type
    fileType
    appType
    webType
    commonType
    subType
    serviceType
    originalFileName
    originalFileSize
    fileExtension
    thumbnail
    path
    groupId
    tags
    status
    engage
    webLink
    location
    zoom
    isShowMarker
    duration
    num_pages
    createdAt
    lastUpdatedDate
    AWSS3ID
    bucket
    isHide
    contentStatus
    emergencyDuration
    currentDate
    startDate
    assetExpired {
      startDate
      endDate
      timezone
    }
  }
`;

export const ASSET_DETAIL_FIELDS = gql`
  fragment AssetDetailFields on Asset {
    ...AssetListFields
    width
    height
    num_pages
    AWSS3ID
    originalAWSS3ID
    originalFileExtension
    createdBy
    accountId
    teamId
    options
    meta
    isDisable
    assetExpired {
      startDate
      endDate
      timezone
    }
    parentGroupInfo {
      _id
      name
      path
      meta
    }
    lastUpdatedByInfo
    createdByInfo
  }
  ${ASSET_LIST_FIELDS}
`;

// --- Queries ---

// NOTE: The ASSETS_QUERY intentionally inlines field selections instead of
// using the ...AssetListFields fragment. The backend's asset resolver uses
// extractFields(info.fieldNodes) to build MongoDB projections, but it doesn't
// resolve GraphQL fragment spreads — it reads AST selection names directly.
// Using a named fragment here would cause the backend to project only
// {AssetListFields: 1} instead of the actual fields, returning empty results.
export const ASSETS_QUERY = gql`
  query Assets(
    $first: Int
    $after: String
    $teamId: String
    $query: QueryAssetInput
    $sort: AssetSort
    $extendParams: ExtendParams
  ) {
    assets(
      first: $first
      after: $after
      teamId: $teamId
      query: $query
      sort: $sort
      extendParams: $extendParams
    ) {
      page {
        edges {
          cursor
          node {
            _id
            type
            fileType
            appType
            webType
            youtubeType
            commonType
            subType
            serviceType
            originalFileName
            originalFileSize
            fileExtension
            thumbnail
            path
            groupId
            teamId
            tags
            status
            engage
            webLink
            embedLink
            location
            isShowMarker
            duration
            num_pages
            createdAt
            lastUpdatedDate
            AWSS3ID
            bucket
            processId
            isHide
            playlistId
            contentStatus
            emergencyDuration
            currentDate
            startDate
            level22renderEngine
            level22requireUserGesture
            level22simulateTouch
            level25renderEngine
            level25requireUserGesture
            level25simulateTouch
            levelOtherrenderEngine
            levelOtherrequireUserGesture
            levelOthersimulateTouch
            requiredLogin
            displayIniFrame
            isAutoScroll
            scrollType
            scrollTo
            pageHeight
            zoom
            preload
            webPageUrl
            speed
            speedValue
            recordTwoFA
            javascriptDelayExecute
            javascriptDelay2FAExecute
            versionJson
            version
            isPasswordMaster
            disable3rdPartyCookies
            secretTwoFA
            location
            isShowMarker
            postsType
            socialProfile
            hashtag
            latitude
            longitude
            layerType
            zoom
            isShowWeatherDetail
            isPlayForecast
            date
            time
            spacing
            textBackground
            textColor
            dtBackground
            dtColor
            fontType
            textBackgroundType
            dtBackgroundType
            fontSize
            textBackgroundOpacity
            dtBackgroundOpacity
            transparent
            currentAppId
            directory
            durationImage
            durationVideo
            snapshotResolution
            snapshotType
            snapshotDuration
            currentAssetId
            deviceId
            shareType
            previewLink
            aspectRatioType
            aspectRatioWidth
            aspectRatioHeight
            placeName
            placeId
            placeGeometry
            isShowMap
            isShowNearbyPlaces
            ratingFilter
            language
            theme
            qr
            postsType
            profile
            displayMode
            title
            webSite
            email
            telephone
            socialProfiles
            action
            subText
            iphone
            android
            selectedAssetURL
            filename
            runEvery
            positionType
            marginUnit
            marginTypeX
            marginTypeY
            marginX
            marginY
            resolution
            resolutionWidth
            resolutionHeight
            animation
            rest
            adSizeType
            adSizeUnit
            adSizeWidth
            adSizeHeight
            scaleImage
            transparency
            muted
            listView
            hasImage
            is24Hr
            is_scroll
            firstDayOfWeek
            sourceApp
            advancedOptions
            scale
            eventColorType
            eventBackgroundColor
            eventTextColor
            assetExpired {
              startDate
              endDate
              timezone
            }
            # Instagram Office Counter — top-level Asset fields needed for
            # manifest hydration. Without these, edit-mode opens with empty
            # Title/Text inputs. "page" here is the FB Page object (not the
            # GraphQL pagination block above) — required for the _pageName
            # computed field whenSet gate.
            title
            description
            isShowQR
            page
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
      extensions {
        groups {
          _id
          name
          type
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          thumbnail
          createdAt
          lastUpdatedDate
          securities {
            userId
            role
          }
          securityTeams
          meta
        }
      }
    }
  }
`;

// Count-only assets query — selects ONLY pageData.total so the backend resolver
// skips the document materialization path (asset.resolver.ts:93-97 gates count()
// on selecting pageData.total). Use for plan-gating checks like
// "how many virtualScreen assets does this team already have?".
export const ASSETS_COUNT_QUERY = gql`
  query AssetsCount($teamId: String, $query: QueryAssetInput) {
    assets(first: 0, teamId: $teamId, query: $query) {
      pageData {
        total
      }
    }
  }
`;

// Batch fetch assets by IDs for realtime subscription cache sync.
// Returns Asset[] (same __typename as list) so Apollo auto-merges into list cache.
// Excludes computed fields (parentGroupInfo, lastUpdatedByInfo, createdByInfo).
export const ASSETS_BY_IDS = gql`
  query AssetsByIds($ids: [String!]!, $teamId: String) {
    assetsByIds(ids: $ids, teamId: $teamId) {
      _id
      type
      fileType
      appType
      webType
      youtubeType
      commonType
      subType
      serviceType
      originalFileName
      originalFileSize
      fileSize
      fileExtension
      thumbnail
      path
      groupId
      tags
      status
      engage
      webLink
      location
      isShowMarker
      duration
      currentDate
      createdAt
      lastUpdatedDate
      AWSS3ID
      bucket
      isHide
      num_pages
      playlistId
      isCaption
      timezone
      repeat
      targetURL
      staticEgress
      text
      refreshInterval
      isAdvancedCollapsed
      isPasswordMaster
      streamingVideoURL
      mute
      isShowChatBox
      twitchURL
      theme
      postsType
      socialProfile
      hashtag
      isShowMarker
      sourceApp
      displayMode
      level22renderEngine
      level22requireUserGesture
      level22simulateTouch
      level25renderEngine
      level25requireUserGesture
      level25simulateTouch
      levelOtherrenderEngine
      levelOtherrequireUserGesture
      levelOthersimulateTouch
      requiredLogin
      displayIniFrame
      isAutoScroll
      scrollType
      scrollTo
      pageHeight
      zoom
      preload
      webPageUrl
      speed
      speedValue
      recordTwoFA
      javascriptDelayExecute
      javascriptDelay2FAExecute
      versionJson
      disable3rdPartyCookies
      secretTwoFA
      folder
      sheetSelected
      options
      repeatInterval
      lastSyncDate
      lastSyncFile
      location
      latitude
      longitude
      layerType
      isShowWeatherDetail
      isPlayForecast
      date
      time
      spacing
      textBackground
      textColor
      dtBackground
      dtColor
      fontType
      textBackgroundType
      dtBackgroundType
      fontSize
      textBackgroundOpacity
      dtBackgroundOpacity
      transparent
      currentAppId
      directory
      durationImage
      durationVideo
      snapshotResolution
      snapshotType
      snapshotDuration
      deviceId
      shareType
      previewLink
      aspectRatioType
      aspectRatioWidth
      aspectRatioHeight
      placeName
      placeId
      placeGeometry
      isShowMap
      isShowNearbyPlaces
      ratingFilter
      language
      qr
      postsType
      profile
      doc_pages
      page
      displayMode
      title
      webSite
      email
      telephone
      socialProfiles
      currentContentType
      currentAssetId
      currentPlaylistId
      currentDeviceIds
      currentTags
      currentBackgroundId
      enableCropZone
      isOAuth
      croppedInfo
      version
      action
      subText
      iphone
      android
      selectedAssetURL
      filename
      runEvery
      positionType
      marginUnit
      marginTypeX
      marginTypeY
      marginX
      marginY
      resolution
      resolutionWidth
      resolutionHeight
      animation
      rest
      adSizeType
      adSizeUnit
      adSizeWidth
      adSizeHeight
      scaleImage
      transparency
      muted
      backgroundType
      backgroundColor
      backgroundOpacity
      showDateTime
      isRandom
      # ─── Instagram Office Counter (and other counter widgets) ───
      # title/page already selected above at this level — only these are new here
      description
      isShowQR
      listView
      hasImage
      is24Hr
      clockType
      primaryColor
      locations
      is_scroll
      firstDayOfWeek
      advancedOptions
      scale
      eventColorType
      eventBackgroundColor
      eventTextColor
      assetExpired {
        startDate
        endDate
        timezone
      }
    }
  }
`;

// Separate query for folder groups — needed because extensions.groups in the
// assets query only resolves parent folders for assets with non-null paths.
// At root level (path: null), no parent folders exist so extensions.groups is
// always empty. This query fetches folders directly from the groups collection.
export const ASSET_GROUPS_QUERY = gql`
  query AssetGroups(
    $query: QueryGroupInput
    $first: Int
    $after: String
    $sort: GroupSort
    $teamId: String
    $extendParams: ExtendParams
  ) {
    groups(
      query: $query
      first: $first
      after: $after
      sort: $sort
      teamId: $teamId
      extendParams: $extendParams
    ) {
      page {
        edges {
          cursor
          node {
            _id
            name
            type
            path
            description
            teamId
            thumbnail
            bucket
            createdAt
            lastUpdatedDate
            isFolderSecurityEdit
            isFolderSecurityView
            securities {
              userId
              role
            }
            securityTeams
            securitiesDetail {
              name
              userId
              role
            }
            securityTeamsDetail {
              name
              _id
            }
            sharedLink
            meta
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
    }
  }
`;

// Fetch folders shared with the current user via folder-level security.
// Uses the dedicated `groupShareds` resolver which filters groups by
// securities/securityTeams matching the authenticated user.
export const GROUP_SHAREDS_QUERY = gql`
  query GroupShareds($teamId: String, $query: QueryGroupInput, $first: Int, $sort: GroupSort) {
    groupShareds(teamId: $teamId, query: $query, first: $first, sort: $sort) {
      page {
        edges {
          node {
            _id
            name
            type
            path
            description
            teamId
            createdBy
            thumbnail
            bucket
            createdAt
            lastUpdatedDate
            isFolderSecurityEdit
            isFolderSecurityView
            securities {
              userId
              role
            }
            securityTeams
            securitiesDetail {
              name
              userId
              role
            }
            securityTeamsDetail {
              name
              _id
            }
            meta
          }
        }
      }
    }
  }
`;

export const ASSET_TAGS_QUERY = gql`
  query AssetTags($teamId: String, $query: QueryAssetInput, $extendParams: ExtendParams) {
    assetTags(teamId: $teamId, query: $query, extendParams: $extendParams)
  }
`;

// NOTE: Inlined for the same reason as ASSETS_QUERY — the getAssetDetailV2
// resolver uses extractField() to conditionally resolve parentGroupInfo,
// lastUpdatedByInfo, and assetExpired. Fragment spreads would cause these
// checks to fail, returning unresolved fields.
export const GET_ASSET_DETAIL_QUERY = gql`
  query GetAssetDetailV2($teamId: String!, $_id: String!) {
    getAssetDetailV2(teamId: $teamId, _id: $_id) {
      _id
      type
      fileType
      appType
      webType
      youtubeType
      commonType
      originalFileName
      originalFileSize
      fileSize
      fileExtension
      thumbnail
      path
      groupId
      tags
      status
      engage
      webLink
      duration
      video_bitrate
      video_codec
      playlistId
      createdAt
      lastUpdatedDate
      width
      height
      num_pages
      doc_pages
      contentStatus
      AWSS3ID
      bucket
      originalAWSS3ID
      originalFileExtension
      createdBy
      accountId
      teamId
      text
      options
      meta
      refreshInterval
      isDisable
      isHide
      assetExpired {
        startDate
        endDate
        timezone
      }
      parentGroupInfo {
        _id
        name
        path
        meta
      }
      lastUpdatedByInfo
      createdByInfo
      isCaption
      isShuffle
      location
      isShowMarker
      liveAt
      repeat
      timezone
      targetURL
      staticEgress
      isAdvancedCollapsed
      isPasswordMaster
      currentType
      profile
      streamingVideoURL
      mute
      isShowChatBox
      twitchURL
      theme
      postsType
      socialProfile
      hashtag
      isShowMarker
      sourceApp
      displayMode
      level22renderEngine
      level22requireUserGesture
      level22simulateTouch
      level25renderEngine
      level25requireUserGesture
      level25simulateTouch
      levelOtherrenderEngine
      levelOtherrequireUserGesture
      levelOthersimulateTouch
      requiredLogin
      displayIniFrame
      isAutoScroll
      scrollType
      scrollTo
      pageHeight
      zoom
      preload
      requestDesktopSite
      newWebView
      webPageUrl
      speed
      speedValue
      recordTwoFA
      javascriptDelayExecute
      javascriptDelay2FAExecute
      versionJson
      disable3rdPartyCookies
      secretTwoFA
      folder
      sheetSelected
      repeatInterval
      lastSyncDate
      lastSyncFile
      advancedOptions
      displayMode
      socialProfile
      hashtag
      location
      latitude
      longitude
      layerType
      isShowWeatherDetail
      isPlayForecast
      date
      time
      spacing
      textBackground
      textColor
      dtBackground
      dtColor
      fontType
      textBackgroundType
      dtBackgroundType
      fontSize
      textBackgroundOpacity
      dtBackgroundOpacity
      transparent
      currentAppId
      directory
      durationImage
      durationVideo
      snapshotResolution
      snapshotType
      snapshotDuration
      deviceId
      shareType
      previewLink
      aspectRatioType
      aspectRatioWidth
      aspectRatioHeight
      placeName
      placeId
      placeGeometry
      isShowMap
      isShowNearbyPlaces
      ratingFilter
      language
      qr
      postsType
      displayMode
      title
      webSite
      email
      telephone
      socialProfiles
      currentContentType
      currentAssetId
      currentPlaylistId
      currentDeviceIds
      currentTags
      currentBackgroundId
      enableCropZone
      isOAuth
      croppedInfo
      version
      action
      subText
      iphone
      android
      selectedAssetURL
      filename
      runEvery
      positionType
      marginUnit
      marginTypeX
      marginTypeY
      marginX
      marginY
      resolution
      resolutionWidth
      resolutionHeight
      animation
      rest
      adSizeType
      adSizeUnit
      adSizeWidth
      adSizeHeight
      scaleImage
      transparency
      muted
      backgroundType
      backgroundColor
      backgroundOpacity
      showDateTime
      isRandom
      quotes
      # ─── Instagram Office Counter (and other counter widgets) ───
      # title already selected above at this level — page is new here
      description
      isShowQR
      page
      listView
      hasImage
      is24Hr
      clockType
      primaryColor
      locations
      is_scroll
      firstDayOfWeek
      scale
      eventColorType
      eventBackgroundColor
      eventTextColor
    }
  }
`;

// Returns the full asset as a JSON object (all fields, no projection).
// Used when appMeta.isGetDetail is true — the SmallApp iframe needs complete data.
export const GET_ASSET_DETAIL_FULL_QUERY = gql`
  query getAssetDetail($_id: String!, $teamId: String) {
    getAssetDetail(_id: $_id, teamId: $teamId)
  }
`;

// --- Mutations ---

// NOTE: Inlines all fields (no fragment) because the backend's extractFields()
// reads AST selection names directly and doesn't resolve fragment spreads.
// Field list matches scio2 ASSET_RESULT_DETAIL_FIELDS for SmallApp iframe compat.
export const SAVE_ASSET = gql`
  mutation SaveAsset($payload: AssetInput!, $teamId: String) {
    saveAsset(payload: $payload, teamId: $teamId) {
      _id
      AWSS3ID
      teamId
      bucket
      commonType
      createdAt
      lastUpdatedDate
      duration
      filename
      thumbnail
      fileType
      type
      video_1080p
      webType
      webLink
      embedLink
      location
      isShowMarker
      youtubeType
      originalAWSS3ID
      originalFileName
      originalFileExtension
      originalFileSize
      fileSize
      path
      subType
      serviceType
      status
      appType
      accountId
      tags
      groupId
      createdBy
      lastUpdatedBy
      lastTeamId
      shareTos
      engage
      processId
      leastDuration
      restDuration
      changeContent
      delay
      rules
      playbackType
      kioskUrl
      timeout
      preloadKioskUrl
      showTouchHereIcon
      touchScreenIcon
      touchScreenIconAssetId
      touchScreenIconUrlDefault
      touchScreenIconSize
      touchScreenIconLocation
      touchIconBlinkingRate
      returnedUrl
      currentAssetId
      currentPlaylistId
      currentContentType
      contentStatus
      emergencyDuration
      startDate
      timezone
      height
      width
      currentDate
      text
      isUpdateFile
      documentDuration
      scale
      stretchAsset
      touchScreenIconUrl
      iFrameAllow
      advancedOptions
      playlistId
      assetRootId
      screenZones {
        name
        id
        currentType
        playbackType
        currentPlaylistId
        currentScheduleId
        currentAssetId
        stretchAsset
        fitAsset
        documentDuration
        currentSelectionDate
        top
        left
        width
        height
        topPixel
        leftPixel
        widthPixel
        heightPixel
        scale
      }
      options
      isDisable
      isHide
      title
      listView
      hasImage
      is24Hr
      clockType
      primaryColor
      locations
      is_scroll
      firstDayOfWeek
      eventColorType
      eventBackgroundColor
      eventTextColor
      assetExpired {
        startDate
        endDate
        timezone
      }
      meta
      refreshInterval
      javascriptMaxRetries
      requestDesktopSite
      isPasswordMaster
      newWebView
      isNormalWebview
      webPageUrl
      javascriptRun
      appVersion
      durationPage
      fileExtension
      sheetList
      speed
      speedValue
      versionApp
      scaleSpct
      horizontalAlignment
      verticalAlignment
      repeatInterval
      profile
      folder
      sheetSelected
      margins
      isMargins
      customViewRegion
      startCell
      endCell
      isCaption
      isShuffle
      liveAt
      repeat
      targetURL
      staticEgress
      isAdvancedCollapsed
      createdByInfo
      lastUpdatedByInfo
      doc_pages
      currentType
      streamingVideoURL
      mute
      autoApprove
      safeFilter
      isShowChatBox
      twitchURL
      theme
      autoPlay
      muted
      postsType
      socialProfile
      hashtag
      isShowMarker
      sourceApp
      displayMode
      disable3rdPartyCookies
      autoJoin
      level22renderEngine
      level22requireUserGesture
      level22simulateTouch
      level25renderEngine
      level25requireUserGesture
      level25simulateTouch
      levelOtherrenderEngine
      levelOtherrequireUserGesture
      levelOthersimulateTouch
      requiredLogin
      displayIniFrame
      isAutoScroll
      scrollType
      scrollTo
      pageHeight
      zoom
      preload
      recordTwoFA
      javascriptDelayExecute
      javascriptDelay2FAExecute
      versionJson
      secretTwoFA
      lastSyncDate
      lastSyncFile
      durationImage
      durationVideo
      page
      location
      latitude
      longitude
      layerType
      isShowWeatherDetail
      isPlayForecast
      date
      time
      spacing
      textBackground
      textColor
      dtBackground
      dtColor
      fontType
      textBackgroundType
      dtBackgroundType
      fontSize
      textBackgroundOpacity
      dtBackgroundOpacity
      transparent
      currentAppId
      directory
      durationImage
      durationVideo
      snapshotResolution
      snapshotType
      snapshotDuration
      deviceId
      shareType
      previewLink
      aspectRatioType
      aspectRatioWidth
      aspectRatioHeight
      placeName
      placeId
      placeGeometry
      isShowMap
      isShowNearbyPlaces
      ratingFilter
      language
      qr
      displayMode
      title
      webSite
      email
      telephone
      socialProfiles
      currentDeviceIds
      currentTags
      currentBackgroundId
      enableCropZone
      isOAuth
      croppedInfo
      version
      action
      subText
      iphone
      android
      selectedAssetURL
      runEvery
      positionType
      marginUnit
      marginTypeX
      marginTypeY
      marginX
      marginY
      resolution
      resolutionWidth
      resolutionHeight
      animation
      rest
      adSizeType
      adSizeUnit
      adSizeWidth
      adSizeHeight
      scaleImage
      transparency
      backgroundType
      backgroundColor
      backgroundOpacity
      showDateTime
      isRandom
      quotes
      # ─── Instagram Office Counter (and other counter widgets) ───
      # title already selected above at this level — only these are new here
      description
      isShowQR
    }
  }
`;

export const DUPLICATE_ASSET = gql`
  mutation DuplicateAsset($teamId: String!, $assetId: String!, $newAssetName: String) {
    duplicateAsset(teamId: $teamId, assetId: $assetId, newAssetName: $newAssetName) {
      _id
      originalFileName
    }
  }
`;

export const DELETE_OBJECTS = gql`
  mutation DeleteObjects($teamId: String, $payload: DeleteObjectInput!) {
    deleteObjects(teamId: $teamId, payload: $payload)
  }
`;

export const SAVE_GROUP = gql`
  mutation SaveGroup($teamId: String, $payload: GroupInput!) {
    saveGroup(teamId: $teamId, payload: $payload) {
      _id
      name
      description
      path
      type
      thumbnail
      bucket
      createdAt
      lastUpdatedDate
      securities {
        userId
        role
      }
      securityTeams
      isFolderSecurityEdit
      isFolderSecurityView
      securitiesDetail {
        name
        userId
        role
      }
      securityTeamsDetail {
        name
        _id
      }
      meta
    }
  }
`;

export const MOVE_TO_GROUP = gql`
  mutation MoveToGroup($teamId: String, $payload: MoveToGroupInput!) {
    moveToGroup(teamId: $teamId, payload: $payload)
  }
`;

export const UPDATE_OBJECT_TAGS = gql`
  mutation UpdateObjectTags($teamId: String, $payload: UpdateObjectTagsInput!) {
    updateObjectTags(teamId: $teamId, payload: $payload)
  }
`;

// --- Upload Options Queries ---

export const GET_FILE_UPLOAD_OPTIONS = gql`
  query GetFileUploadOptions($payload: UpdateObjectInput!) {
    getFileUploadOptions(payload: $payload)
  }
`;

export const GET_RAW_FILE_UPLOAD_OPTIONS = gql`
  query GetRawFileUploadOptions($payload: UpdateObjectRawInput!, $teamId: String) {
    getRawFileUploadOptions(payload: $payload, teamId: $teamId)
  }
`;

export const GET_FILE_UPLOAD_THUMBNAIL_OPTIONS = gql`
  query getFileUploadThumbnailOptions($payload: UpdateObjectThumbnailInput!, $teamId: String) {
    getFileUploadThumbnailOptions(payload: $payload, teamId: $teamId)
  }
`;

// --- Preview ---

export const GET_PREVIEW_LINK = gql`
  query GetPreviewLink($payload: PreviewLinkInput!, $teamId: String) {
    getPreviewLink(payload: $payload, teamId: $teamId)
  }
`;

export const GENERATE_SHORT_URL = gql`
  query GenerateShortURL($payload: GenerateShortURLInput!) {
    generateShortURL(payload: $payload)
  }
`;

// --- Emergency / Flash Message ---

export const DO_EMERGENCY_MESSAGE = gql`
  mutation DoEmergencyMessage($payload: AssetMessageInput!, $teamId: String) {
    doEmergencyMessage(payload: $payload, teamId: $teamId)
  }
`;

export const DO_FLASH_MESSAGE = gql`
  mutation DoFlashMessage($payload: AssetMessageInput!, $teamId: String) {
    doFlashMessage(payload: $payload, teamId: $teamId)
  }
`;

// --- Asset Expired (Live/Expire) ---

export const GET_ASSET_EXPIRE = gql`
  query GetAssetExpire($_id: String!, $teamId: String!) {
    getAssetExpire(_id: $_id, teamId: $teamId) {
      _id
      range {
        startDate
        endDate
      }
      timezone
      scheduleId
      type
      is_delete
    }
  }
`;

export const UPDATE_ASSET_EXPIRES = gql`
  mutation UpdateAssetExpires($teamId: String!, $payload: AssetExpiredInput!) {
    updateAssetExpires(teamId: $teamId, payload: $payload)
  }
`;

export const DELETE_ASSET_EXPIRES = gql`
  mutation DeleteAssetExpires($teamId: String!, $payload: AssetExpiredInput!) {
    deleteAssetExpires(teamId: $teamId, payload: $payload)
  }
`;

// --- Recent Assets ---

// NOTE: Inlines fields (no fragment) because the backend's extractFields()
// reads AST selection names directly and doesn't resolve fragment spreads.
// Used by Engage "Recent" section — queries the recentData collection
// filtered by type (ENGAGE) and returns the associated assets.
export const ASSET_RECENTS_QUERY = gql`
  query AssetRecents(
    $first: Int
    $after: String
    $query: QueryRecentDataInput
    $sort: DataRecentSort
    $teamId: String
  ) {
    assetRecents(first: $first, after: $after, query: $query, sort: $sort, teamId: $teamId) {
      page {
        edges {
          node {
            _id
            type
            fileType
            appType
            webType
            commonType
            subType
            serviceType
            originalFileName
            originalFileSize
            fileExtension
            thumbnail
            path
            groupId
            tags
            status
            engage
            webLink
            duration
            num_pages
            createdAt
            lastUpdatedDate
            AWSS3ID
            bucket
            isHide
            assetExpired {
              startDate
              endDate
              timezone
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Recent Data ---

export const SAVE_RECENT_DATA = gql`
  mutation SaveRecentData($payload: DataRecentInput!, $teamId: String) {
    saveRecentData(payload: $payload, teamId: $teamId)
  }
`;

// --- SmallApp Token ---

// Legacy SmallApp init flow: generates a custom JWT for iframe SmallApp auth.
// Used by _sendToken (INIT event) — NOT used by _sendTokenNew (ON_INIT event).
export const TOKEN_ASSET_SMALLAPP = gql`
  mutation TokenAssetSmallApp($appType: String, $assetId: String, $teamId: String) {
    tokenAssetSmallApp(appType: $appType, assetId: $assetId, teamId: $teamId)
  }
`;

// --- Hidden-Asset Signage URLs (OptiEdge MDM Phase 1 — DIY paste) ---
// Hidden assets back the per-vendor "Signage URLs" tab. Each row is one URL
// the customer pastes at the OU / device-group level in Google Admin Console
// or MS Teams Pro Mgmt portal. Filter:vendor scopes the list to the active
// per-vendor Connect page.

export const HIDDEN_ASSET_SIGNAGE_URLS = gql`
  query HiddenAssetSignageUrls($vendor: String!) {
    hiddenAssetSignageUrls(vendor: $vendor) {
      _id
      name
      type
      meta
      signageUrl
      createdAt
      lastUpdatedDate
    }
  }
`;

export const CREATE_HIDDEN_ASSET_SIGNAGE_URL = gql`
  mutation CreateHiddenAssetSignageUrl($input: CreateHiddenAssetSignageUrlInput!) {
    createHiddenAssetSignageUrl(input: $input) {
      _id
      name
      type
      meta
      signageUrl
    }
  }
`;

export const UPDATE_HIDDEN_ASSET_SIGNAGE_URL = gql`
  mutation UpdateHiddenAssetSignageUrl($input: UpdateHiddenAssetSignageUrlInput!) {
    updateHiddenAssetSignageUrl(input: $input) {
      _id
      name
      type
      meta
      signageUrl
    }
  }
`;

export const ASSIGN_CONTENT_TO_HIDDEN_ASSET_SIGNAGE_URL = gql`
  mutation AssignContentToHiddenAssetSignageUrl(
    $_id: String!
    $contentType: String!
    $contentId: String!
  ) {
    assignContentToHiddenAssetSignageUrl(
      _id: $_id
      contentType: $contentType
      contentId: $contentId
    ) {
      _id
      meta
    }
  }
`;

export const DELETE_HIDDEN_ASSET_SIGNAGE_URL = gql`
  mutation DeleteHiddenAssetSignageUrl($_id: String!) {
    deleteHiddenAssetSignageUrl(_id: $_id)
  }
`;

/**
 * Direct DO Spaces (or S3 fallback) presigned URL for Copy URL.
 * Backend delegates to FileStorageService — returns a scio1-shape URL
 * (V2 signature, CDN endpoint, 30-day expiry) that OptiSync can auto-renew.
 */
export const GET_FILE_SIGNED_URL = gql`
  query GetFileSignedUrl($key: String!, $bucket: String) {
    getFileSignedUrl(key: $key, bucket: $bucket)
  }
`;
